import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, TrendingDown } from "lucide-react";

const FallWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const fallTab = tabs.find((tab) => tab.id === "fall");
    const widget = fallTab?.widget || {};

    return (
        <motion.div className="relative w-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.5 }}>
            {/* Main card */}
            <div className="bg-gradient-to-br from-white/95 to-white/85 backdrop-blur-lg rounded-3xl p-6 shadow-xl border border-red-100/50">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                        <motion.div
                            className={`w-10 h-10 rounded-2xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center me-3 shadow-sm`}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </motion.div>
                        <div>
                            <h5 className="text-red-800 font-semibold text-lg">{widget.title}</h5>
                            <p className="text-red-600/70 text-sm">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.span
                        className="text-sm bg-gradient-to-r from-green-100 to-green-50 text-green-700 px-4 py-2 rounded-full font-medium shadow-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        ✓ {widget.status}
                    </motion.span>
                </div>

                {/* Circular main display */}
                <div className="relative flex items-center justify-center mb-6">
                    <div className="relative w-32 h-32">
                        {/* Background circle */}
                        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="50" fill="none" stroke="#fee2e2" strokeWidth="8" />
                            <motion.circle
                                cx="60"
                                cy="60"
                                r="50"
                                fill="none"
                                stroke="url(#redGradient)"
                                strokeWidth="8"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 50}`}
                                initial={{ strokeDashoffset: 2 * Math.PI * 50 }}
                                animate={{ strokeDashoffset: 2 * Math.PI * 50 * (1 - 0.85) }}
                                transition={{ duration: 2, ease: "easeOut" }}
                            />
                            <defs>
                                <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#ef4444" />
                                    <stop offset="100%" stopColor="#dc2626" />
                                </linearGradient>
                            </defs>
                        </svg>

                        {/* Center content */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.div className="text-2xl font-bold text-red-700" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, duration: 0.5 }}>
                                {widget.accuracy}
                            </motion.div>
                            <div className="text-xs text-red-600/70 text-center">{widget.accuracyLabel}</div>
                        </div>
                    </div>
                </div>

                {/* Today's status */}
                <div className="text-center mb-4">
                    <p className="text-gray-600 text-sm mb-1">{widget.todayStatus?.label}</p>
                    <div className="flex items-center justify-center">
                        <motion.div className="w-2 h-2 rounded-full bg-green-500 ml-2" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                        <span className="text-green-700 font-medium">{widget.todayStatus?.value}</span>
                    </div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-4 mt-4">
                {/* Weekly stats */}
                <motion.div
                    className="bg-gradient-to-br from-white/95 to-white/85 backdrop-blur-lg rounded-2xl p-4 border border-blue-100/50 shadow-lg"
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-blue-700 font-medium text-sm">{widget.weeklyStats?.label}</span>
                        <motion.div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center" whileHover={{ rotate: 10 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#3b82f6" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 3V21H21" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 9L12 6L16 10L21 5" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </motion.div>
                    </div>

                    {/* Mini wave chart */}
                    <div className="relative h-16 mb-3">
                        <svg className="w-full h-full" viewBox="0 0 200 60">
                            <motion.path
                                d="M10,45 Q50,20 90,35 T170,25"
                                fill="none"
                                stroke="url(#blueWaveGradient)"
                                strokeWidth="3"
                                strokeLinecap="round"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 2, ease: "easeInOut" }}
                            />
                            <defs>
                                <linearGradient id="blueWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#60a5fa" />
                                    <stop offset="100%" stopColor="#3b82f6" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{widget.weeklyStats?.period}</span>
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-md font-medium">{widget.weeklyStats?.value}</span>
                    </div>
                </motion.div>

                {/* Sensitivity settings */}
                <motion.div
                    className="bg-gradient-to-br from-white/95 to-white/85 backdrop-blur-lg rounded-2xl p-4 border border-orange-100/50 shadow-lg"
                    whileHover={{ scale: 1.02, y: -2 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-orange-700 font-medium text-sm">{widget.sensitivity?.label}</span>
                        <motion.div className="w-6 h-6 rounded-lg bg-orange-100 flex items-center justify-center" whileHover={{ scale: 1.1 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#f97316" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </motion.div>
                    </div>

                    {/* Small circular progress */}
                    <div className="relative flex items-center justify-center mb-3">
                        <div className="relative w-16 h-16">
                            <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 60 60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#fed7aa" strokeWidth="4" />
                                <motion.circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#f97316"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeDasharray={`${2 * Math.PI * 25}`}
                                    initial={{ strokeDashoffset: 2 * Math.PI * 25 }}
                                    animate={{ strokeDashoffset: 2 * Math.PI * 25 * (1 - 0.65) }}
                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-bold text-orange-700">{widget.sensitivity?.value}</span>
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <span className="text-xs bg-orange-50 text-orange-600 px-2 py-1 rounded-md font-medium">{widget.sensitivity?.level}</span>
                    </div>
                </motion.div>
            </div>

            {/* Tips section */}
            <motion.div
                className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
            >
                <div className="flex items-center mb-2">
                    <Info className={`w-[14px] h-[14px] ${isRTL ? "mr-1.5" : "ml-1.5"} mt-[2px] text-green-600`} strokeWidth={2} />
                    <span className="text-green-800 font-medium text-sm">{widget.tips?.title}</span>
                </div>
                <p className="text-green-700 text-xs leading-relaxed">{widget.tips?.text}</p>
            </motion.div>
        </motion.div>
    );
};

export default FallWidget;
